import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { Visibility, VisibilityOff, CloudUpload } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import axiosInstance from "../../../utils/axiosInstance";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { createUser, updateUser } from "../../../Redux/Reducers/UserRoleReducer";
import { getFullUrl } from "../../../utils/globalUtils";
import { useTranslation } from "react-i18next";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
  roleId: "",
  status: "Active",
  image: null,
};

const UserFormModal = ({ open, onClose, userId = null, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { loading } = useSelector((state) => state.UserRoleReducer);

  const [roles, setRoles] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: EMPTY_FORM,
  });

  const password = watch("password");

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      setInitialLoading(true);

      try {
        // Fetch roles
        const roleRes = await axiosInstance.get("/api/admin/roles?type=dropdown");
        setRoles(roleRes.data?.data || []);

        if (userId) {
          // EDIT MODE
          const userRes = await axiosInstance.get(`/api/admin/users/${userId}`);
          const user = userRes.data?.data;

          reset({
            name: user?.name || "",
            email: user?.email || "",
            phone: user?.phone || "",
            roleId: user?.role?.id?.toString() || "",
            status: user?.isActive ? t('active', "Active") : t('inactive', "Inactive"),
            password: "",
            confirmPassword: "",
            image: null,
          });

          setImagePreview(user?.profileImage ? getFullUrl(user.profileImage, "user") : null);
        } else {
          // CREATE MODE (FORCE CLEAN RESET)
          reset(EMPTY_FORM);
          setImagePreview(null);
        }
      } catch (err) {
        toast.error("Failed to load data");
      } finally {
        setInitialLoading(false);
      }
    };

    loadData();
  }, [open, userId, reset]);

  /* ================= IMAGE HANDLER ================= */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t('administrator.messages.image_allowed_validate', "Only image files allowed"));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      // toast.error(t('administrator.messages.image_size_validate',"Image must be under 5MB"));
      toast.error(
        t('administrator.messages.image_size_validate', {
          defaultValue: 'Image must be under 5MB'
        })
      );
      return;
    }

    setValue("image", file);
    setImagePreview(URL.createObjectURL(file));
  };

  /* ================= SUBMIT ================= */
  const onSubmit = async (data) => {
    try {
      const payload = new FormData();
      payload.append("name", data.name);
      payload.append("email", data.email);
      payload.append("phone", data.phone);
      payload.append("roleId", Number(data.roleId));
      payload.append("isActive", data.status === "Active");

      if (data.password) payload.append("password", data.password);
      if (data.image) payload.append("profileImage", data.image);

      if (userId) {
        await dispatch(updateUser({ id: userId, payload })).unwrap();
        toast.success(t('administrator.messages.update_success', "User updated successfully"));
      } else {
        await dispatch(createUser(payload)).unwrap();
        toast.success(t('administrator.messages.create_success', "User created successfully"));
      }

      handleClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err?.message || t('administrator.messages.save_failed', "Save failed"));
    }
  };

  /* ================= CLOSE ================= */
  const handleClose = () => {
    if (loading) return;

    reset(EMPTY_FORM);
    setImagePreview(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
        {userId ? t('administrator.popup_titles.edit_user', "Edit Administrator") : t('administrator.popup_titles.create_user', "Create Administrator")}
        <IconButton onClick={handleClose} disabled={loading} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          borderColor: 'rgba(230, 210, 249, 0.1)',
          padding: '24px 16px',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(230, 210, 249, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(230, 210, 249, 0.5)',
            },
          },
        }}
      >
        {initialLoading ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <form id="user-form" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  {...register("name", { required: t('administrator.validations.name_required', "Name is required") })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  placeholder={t('name', 'Name')}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-input': {
                      padding: '12px 20px',
                      fontWeight: '400',
                      color: '#fff',
                    },
                    '& input::placeholder': {
                      color: 'rgba(255,255,255,0.6)',
                      opacity: 1,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} sx={{ pr: { md: '8px' } }}>
                <TextField
                  fullWidth
                  {...register("email", { required: t('administrator.validations.email_required', "Email is required") })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  placeholder={t('email', "Email")}
                  autoComplete="off"
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-input': {
                      padding: '12px 20px',
                      fontWeight: '400',
                      color: '#fff',
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                        WebkitTextFillColor: '#fff',
                        transition: 'background-color 9999s ease-in-out 0s',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                      },
                    },
                    '& input::placeholder': {
                      color: 'rgba(255,255,255,0.6)',
                      opacity: 1,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} sx={{ pl: { md: '8px' } }}>
                <TextField
                  fullWidth
                  {...register("phone", {
                    required: t('administrator.validations.phone_required', "Phone is required"), pattern: {
                      value: /^[0-9]{10}$/,
                      message: t('administrator.validations.invalid_phone', "Phone number must be between 10  digits"),
                    },
                  })}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    register("phone").onChange(e);
                  }}
                  onKeyDown={(e) => {
                    if (
                      [
                        'Backspace',
                        'Delete',
                        'Tab',
                        'Escape',
                        'Enter',
                        'ArrowLeft',
                        'ArrowRight',
                        'ArrowUp',
                        'ArrowDown',
                        'Home',
                        'End',
                      ].includes(e.key) ||
                      (e.ctrlKey || e.metaKey)
                    ) {
                      return;
                    }
                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                    maxLength: 10,
                  }}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  placeholder={t('phone', "Phone")}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-input': {
                      padding: '12px 20px',
                      fontWeight: '400',
                      color: '#fff',
                      '&:-webkit-autofill': {
                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                        WebkitTextFillColor: '#fff',
                        transition: 'background-color 9999s ease-in-out 0s',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                      },
                    },
                    '& input::placeholder': {
                      color: 'rgba(255,255,255,0.6)',
                      opacity: 1,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} sx={{ pr: { md: '8px' } }}>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  {...register("password", {
                    required: !userId
                      ? t('administrator.validations.password_required', "Password is required")
                      : false,

                    minLength: {
                      value: 8,
                      message: t(
                        'administrator.validations.password_min_length',
                        "Password must be at least 8 characters"
                      ),
                    },

                    pattern: {
                      value:
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
                      message: t(
                        'administrator.validations.password_pattern',
                        "Password must contain uppercase, lowercase, number and special character"
                      ),
                    },
                  })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  placeholder={t('administrator.fields.password', "Password")}
                  autoComplete="new-password"
                  sx={{
                    mb: '16px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '999px',
                      backgroundColor: 'transparent',
                      color: '#fff',
                      fontSize: '15px',
                      '& fieldset': {
                        borderColor: 'rgba(230, 210, 249, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(230, 210, 249, 0.2)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(230, 210, 249, 0.2)',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '12px 20px',
                      color: '#fff',
                    },
                    '& input::placeholder': {
                      color: 'rgba(255,255,255,0.6)',
                      opacity: 1,
                    },
                    '& .MuiFormHelperText-root': {
                      ml: '20px'
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" sx={{ mr: '0px' }}>
                          {!showPassword ?
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M9.46992 15.28C9.27992 15.28 9.08992 15.21 8.93992 15.06C8.11992 14.24 7.66992 13.15 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C13.1499 7.67004 14.2399 8.12004 15.0599 8.94004C15.1999 9.08004 15.2799 9.27004 15.2799 9.47004C15.2799 9.67004 15.1999 9.86004 15.0599 10L9.99992 15.06C9.84992 15.21 9.65992 15.28 9.46992 15.28ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 12.5 9.29992 12.98 9.53992 13.4L13.3999 9.54004C12.9799 9.30004 12.4999 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                              <path d="M5.59984 18.51C5.42984 18.51 5.24984 18.45 5.10984 18.33C4.03984 17.42 3.07984 16.3 2.25984 15C1.19984 13.35 1.19984 10.66 2.25984 8.99998C4.69984 5.17998 8.24984 2.97998 11.9998 2.97998C14.1998 2.97998 16.3698 3.73998 18.2698 5.16998C18.5998 5.41998 18.6698 5.88998 18.4198 6.21998C18.1698 6.54998 17.6998 6.61998 17.3698 6.36998C15.7298 5.12998 13.8698 4.47998 11.9998 4.47998C8.76984 4.47998 5.67984 6.41998 3.51984 9.80998C2.76984 10.98 2.76984 13.02 3.51984 14.19C4.26984 15.36 5.12984 16.37 6.07984 17.19C6.38984 17.46 6.42984 17.93 6.15984 18.25C6.01984 18.42 5.80984 18.51 5.59984 18.51Z" fill="#FF69B4" />
                              <path d="M12.0001 21.02C10.6701 21.02 9.37006 20.75 8.12006 20.22C7.74006 20.06 7.56006 19.62 7.72006 19.24C7.88006 18.86 8.32006 18.68 8.70006 18.84C9.76006 19.29 10.8701 19.52 11.9901 19.52C15.2201 19.52 18.3101 17.58 20.4701 14.19C21.2201 13.02 21.2201 10.98 20.4701 9.81C20.1601 9.32 19.8201 8.85 19.4601 8.41C19.2001 8.09 19.2501 7.62 19.5701 7.35C19.8901 7.09 20.3601 7.13 20.6301 7.46C21.0201 7.94 21.4001 8.46 21.7401 9C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02Z" fill="#FF69B4" />
                              <path d="M12.6901 16.27C12.3401 16.27 12.0201 16.02 11.9501 15.66C11.8701 15.25 12.1401 14.86 12.5501 14.79C13.6501 14.59 14.5701 13.67 14.7701 12.57C14.8501 12.16 15.2401 11.9 15.6501 11.97C16.0601 12.05 16.3301 12.44 16.2501 12.85C15.9301 14.58 14.5501 15.95 12.8301 16.27C12.7801 16.26 12.7401 16.27 12.6901 16.27Z" fill="#FF69B4" />
                              <path d="M1.99994 22.75C1.80994 22.75 1.61994 22.68 1.46994 22.53C1.17994 22.24 1.17994 21.76 1.46994 21.47L8.93994 14C9.22994 13.71 9.70994 13.71 9.99994 14C10.2899 14.29 10.2899 14.77 9.99994 15.06L2.52994 22.53C2.37994 22.68 2.18994 22.75 1.99994 22.75Z" fill="#FF69B4" />
                              <path d="M14.5302 10.2199C14.3402 10.2199 14.1502 10.1499 14.0002 9.99994C13.7102 9.70994 13.7102 9.22994 14.0002 8.93994L21.4702 1.46994C21.7602 1.17994 22.2402 1.17994 22.5302 1.46994C22.8202 1.75994 22.8202 2.23994 22.5302 2.52994L15.0602 9.99994C14.9102 10.1499 14.7202 10.2199 14.5302 10.2199Z" fill="#FF69B4" />
                            </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M11.9999 16.33C9.60992 16.33 7.66992 14.39 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C14.3899 7.67004 16.3299 9.61004 16.3299 12C16.3299 14.39 14.3899 16.33 11.9999 16.33ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 13.56 10.4399 14.83 11.9999 14.83C13.5599 14.83 14.8299 13.56 14.8299 12C14.8299 10.44 13.5599 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                              <path d="M12.0001 21.02C8.24008 21.02 4.69008 18.82 2.25008 15C1.19008 13.35 1.19008 10.66 2.25008 8.99998C4.70008 5.17998 8.25008 2.97998 12.0001 2.97998C15.7501 2.97998 19.3001 5.17998 21.7401 8.99998C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02ZM12.0001 4.47998C8.77008 4.47998 5.68008 6.41998 3.52008 9.80998C2.77008 10.98 2.77008 13.02 3.52008 14.19C5.68008 17.58 8.77008 19.52 12.0001 19.52C15.2301 19.52 18.3201 17.58 20.4801 14.19C21.2301 13.02 21.2301 10.98 20.4801 9.80998C18.3201 6.41998 15.2301 4.47998 12.0001 4.47998Z" fill="#FF69B4" />
                            </svg>}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} sx={{ pl: { md: '8px' } }}>
                <TextField
                  fullWidth
                  type={showConfirmPassword ? "text" : "password"}
                  {...register("confirmPassword", {
                    required: !userId ? t(
                      'administrator.validations.confirm_password_required',
                      "Confirm password is required"
                    ) : false,

                    validate: (value) => {
                      // if password is empty during edit, no validation needed
                      if (!password) return true;

                      return (
                        value === password ||
                        t(
                          'administrator.validations.password_not_matched',
                          "Passwords do not match"
                        )
                      );
                    },
                  })}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  placeholder={t('administrator.fields.confirm_password', "Confirm Password")}
                  sx={{
                    mb: '16px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '999px',
                      backgroundColor: 'transparent',
                      color: '#fff',
                      fontSize: '15px',
                      '& fieldset': {
                        borderColor: 'rgba(230, 210, 249, 0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(230, 210, 249, 0.2)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(230, 210, 249, 0.2)',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '12px 20px',
                      color: '#fff',
                    },
                    '& input::placeholder': {
                      color: 'rgba(255,255,255,0.6)',
                      opacity: 1,
                    },
                    '& .MuiFormHelperText-root': {
                      ml: '20px'
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ mr: '0px' }}>
                          {!showConfirmPassword ? <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M9.46992 15.28C9.27992 15.28 9.08992 15.21 8.93992 15.06C8.11992 14.24 7.66992 13.15 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C13.1499 7.67004 14.2399 8.12004 15.0599 8.94004C15.1999 9.08004 15.2799 9.27004 15.2799 9.47004C15.2799 9.67004 15.1999 9.86004 15.0599 10L9.99992 15.06C9.84992 15.21 9.65992 15.28 9.46992 15.28ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 12.5 9.29992 12.98 9.53992 13.4L13.3999 9.54004C12.9799 9.30004 12.4999 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                            <path d="M5.59984 18.51C5.42984 18.51 5.24984 18.45 5.10984 18.33C4.03984 17.42 3.07984 16.3 2.25984 15C1.19984 13.35 1.19984 10.66 2.25984 8.99998C4.69984 5.17998 8.24984 2.97998 11.9998 2.97998C14.1998 2.97998 16.3698 3.73998 18.2698 5.16998C18.5998 5.41998 18.6698 5.88998 18.4198 6.21998C18.1698 6.54998 17.6998 6.61998 17.3698 6.36998C15.7298 5.12998 13.8698 4.47998 11.9998 4.47998C8.76984 4.47998 5.67984 6.41998 3.51984 9.80998C2.76984 10.98 2.76984 13.02 3.51984 14.19C4.26984 15.36 5.12984 16.37 6.07984 17.19C6.38984 17.46 6.42984 17.93 6.15984 18.25C6.01984 18.42 5.80984 18.51 5.59984 18.51Z" fill="#FF69B4" />
                            <path d="M12.0001 21.02C10.6701 21.02 9.37006 20.75 8.12006 20.22C7.74006 20.06 7.56006 19.62 7.72006 19.24C7.88006 18.86 8.32006 18.68 8.70006 18.84C9.76006 19.29 10.8701 19.52 11.9901 19.52C15.2201 19.52 18.3101 17.58 20.4701 14.19C21.2201 13.02 21.2201 10.98 20.4701 9.81C20.1601 9.32 19.8201 8.85 19.4601 8.41C19.2001 8.09 19.2501 7.62 19.5701 7.35C19.8901 7.09 20.3601 7.13 20.6301 7.46C21.0201 7.94 21.4001 8.46 21.7401 9C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02Z" fill="#FF69B4" />
                            <path d="M12.6901 16.27C12.3401 16.27 12.0201 16.02 11.9501 15.66C11.8701 15.25 12.1401 14.86 12.5501 14.79C13.6501 14.59 14.5701 13.67 14.7701 12.57C14.8501 12.16 15.2401 11.9 15.6501 11.97C16.0601 12.05 16.3301 12.44 16.2501 12.85C15.9301 14.58 14.5501 15.95 12.8301 16.27C12.7801 16.26 12.7401 16.27 12.6901 16.27Z" fill="#FF69B4" />
                            <path d="M1.99994 22.75C1.80994 22.75 1.61994 22.68 1.46994 22.53C1.17994 22.24 1.17994 21.76 1.46994 21.47L8.93994 14C9.22994 13.71 9.70994 13.71 9.99994 14C10.2899 14.29 10.2899 14.77 9.99994 15.06L2.52994 22.53C2.37994 22.68 2.18994 22.75 1.99994 22.75Z" fill="#FF69B4" />
                            <path d="M14.5302 10.2199C14.3402 10.2199 14.1502 10.1499 14.0002 9.99994C13.7102 9.70994 13.7102 9.22994 14.0002 8.93994L21.4702 1.46994C21.7602 1.17994 22.2402 1.17994 22.5302 1.46994C22.8202 1.75994 22.8202 2.23994 22.5302 2.52994L15.0602 9.99994C14.9102 10.1499 14.7202 10.2199 14.5302 10.2199Z" fill="#FF69B4" />
                          </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M11.9999 16.33C9.60992 16.33 7.66992 14.39 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C14.3899 7.67004 16.3299 9.61004 16.3299 12C16.3299 14.39 14.3899 16.33 11.9999 16.33ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 13.56 10.4399 14.83 11.9999 14.83C13.5599 14.83 14.8299 13.56 14.8299 12C14.8299 10.44 13.5599 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                            <path d="M12.0001 21.02C8.24008 21.02 4.69008 18.82 2.25008 15C1.19008 13.35 1.19008 10.66 2.25008 8.99998C4.70008 5.17998 8.25008 2.97998 12.0001 2.97998C15.7501 2.97998 19.3001 5.17998 21.7401 8.99998C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02ZM12.0001 4.47998C8.77008 4.47998 5.68008 6.41998 3.52008 9.80998C2.77008 10.98 2.77008 13.02 3.52008 14.19C5.68008 17.58 8.77008 19.52 12.0001 19.52C15.2301 19.52 18.3201 17.58 20.4801 14.19C21.2301 13.02 21.2301 10.98 20.4801 9.80998C18.3201 6.41998 15.2301 4.47998 12.0001 4.47998Z" fill="#FF69B4" />
                          </svg>}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6} sx={{ pr: { md: '8px' }, mb: { xs: '16px', md: 0 } }}>
                <Controller
                  name="roleId"
                  control={control}
                  rules={{ required: t('administrator.validations.role_required', "Role is required") }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      //label="Role"
                      error={!!errors.roleId}
                      helperText={errors.roleId?.message}
                      SelectProps={{
                        displayEmpty: true,
                      }}
                      sx={{
                        color: "#fff",
                        fontWeight: 400,
                        "& .MuiSelect-select": {
                          padding: "12px 20px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1) !important",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#fff",
                        },
                      }}
                    >
                      <MenuItem value="">
                        <Box component="span" sx={{ color: 'rgba(255,255,255,0.5)' }}>{t('role', "Role")}</Box>
                      </MenuItem>
                      {roles.map((r) => (
                        <MenuItem key={r.id} value={r.id.toString()}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6} sx={{ pl: { md: '8px' } }}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      sx={{
                        color: "#fff",
                        fontWeight: 400,
                        "& .MuiSelect-select": {
                          padding: "12px 20px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1) !important",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#fff",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>{t('administrator.fields.select_status', "Select Status")}</MenuItem>
                      <MenuItem value="Active">{t('active', "Active")}</MenuItem>
                      <MenuItem value="Inactive">{t('inactive', "Inactive")}</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} sx={{ mt: '16px' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '400', fontSize: '14px' }}>
                    {t("administrator.fields.upload_image_title", "Upload Profile Image")} <Typography component="span" sx={{ fontSize: '13px', color: '#ff4d4f', ml: 1 }}>{t('administrator.fields.upload_square_only', '(Upload Only Square Image)')}</Typography>
                </Typography>
                <Box
                  component="label"
                  onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const file = e.dataTransfer.files[0];
                      if (file) {
                          handleImageChange({ target: { files: [file] } });
                      }
                  }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  sx={{
                    border: "1px dashed rgba(230, 210, 249, 0.2)",
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    borderRadius: "999px",
                  }}
                >
                  <input hidden type="file" accept="image/png, image/jpg, image/webp" onChange={handleImageChange} />

                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      width={80}
                      alt={t('preview', 'Preview')}
                      style={{ borderRadius: '8px', objectFit: 'cover' }}
                    />
                  ) : (
                    <>
                      <Box sx={{ pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CloudUpload fontSize="large" />

                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: '14px',
                              my: '3px',
                              textAlign: 'center'
                            }}
                          >
                            {t(
                              'administrator.fields.upload_image',
                              'Upload Image (File must be less than 5MB)'
                            )}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '13px',
                              color: '#ff4d4f',
                              textAlign: 'center',
                              mb: '4px'
                            }}
                          >
                            {t('administrator.fields.upload_square_only', '(Upload Only Square Image)')}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '12px',
                              color: 'rgba(255,255,255,0.6)',
                              textAlign: 'center'
                            }}
                          >
                            {t(
                              'administrator.fields.click_browse',
                              'or click to browse (Supported formats: PNG, JPG, WEBP)'
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
        <Button onClick={handleClose} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>{t('cancel', 'Cancel')}</Button>
        <Button
          type="submit"
          form="user-form"
          variant="contained"
          disabled={loading || initialLoading}
          sx={{
            py: 1.5,
            borderRadius: '999px',
            fontWeight: '400',
            fontSize: '15px',
            lineHeight: '20px',
            textTransform: 'none',
            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
            textTransform: 'capitalize',
            minWidth: { xs: '100%', sm: '150px' },
            marginLeft: '10px',
          }}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : userId ? (
            t('update', "Update")
          ) : (
            t('create', "Create")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormModal;
